import { Input, Toogle } from 'alurkerja-ui'
import { stringToTitleCase } from '@/utils/functions/stringToTitleCase'
import React, { useMemo, useCallback } from 'react'

type EditTooltipProps = {
  index: number
  element: string
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
}

const MAX_MESSAGE_LENGTH = 255

export default function EditTooltip(props: EditTooltipProps) {
  const { index, element, currentElementState, setCurrentElementState } = props

  // Properly memoize the enable value with both dependencies
  const enableValue = useMemo(() => currentElementState[element]?.enable, [currentElementState, element])

  // Get the message value with proper memoization
  const messageValue = useMemo(() => currentElementState[element]?.message || '', [currentElementState, element])

  // Memoize the toggle onChange handler to prevent recreating it on each render
  const handleToggleChange = useCallback(
    (value: string | number | boolean | undefined) => {
      setCurrentElementState((prevState: any) => {
        // Only update if the value is actually different
        if (prevState[element]?.enable === value) {
          return prevState
        }
        return {
          ...prevState,
          [element]: {
            ...prevState[element],
            enable: value,
          },
        }
      })
    },
    [element, setCurrentElementState]
  )

  // Memoize the input onChange handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      // Validate max 225 characters
      if (newValue.length > MAX_MESSAGE_LENGTH) {
        return // Don't update if exceeds MAX_MESSAGE_LENGTH characters
      }

      setCurrentElementState((prevState: any) => {
        // Only update if the value is actually different
        if (prevState[element]?.message === newValue) {
          return prevState
        }
        return {
          ...prevState,
          [element]: {
            ...prevState[element],
            message: newValue,
          },
        }
      })
    },
    [element, setCurrentElementState]
  )

  return React.useMemo(
    () => (
      <div className="flex flex-col gap-0 px-3 pt-2">
        <div key={index} className="pb-1 flex gap-2 rounded-sm">
          <span className="font-semibold">{stringToTitleCase(element)}</span>
          <Toogle defaultValue={enableValue} onChange={handleToggleChange} />
        </div>
        <div className="flex flex-col gap-1">
          <Input
            className="w-full"
            placeholder={'Type message for tooltip here'}
            type="text"
            size="sm"
            rounded="lg"
            value={messageValue}
            onChange={handleInputChange}
          />
          <div className="text-xs text-gray-500 text-right">
            <span className={messageValue.length > MAX_MESSAGE_LENGTH - 1 ? 'text-red-500' : ''}>
              {messageValue.length}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
        </div>
      </div>
    ),
    [index, element, enableValue, messageValue, handleToggleChange, handleInputChange]
  )
}
